import { createFileRoute, notFound } from "@tanstack/react-router";
import { stateKeywordsMeta } from "@/lib/page-keywords";
import { getProduct } from "@/data/products";
import { getProductHero } from "@/data/productHeroes";
import { getStateHero } from "@/data/state-heroes";
import { getState } from "@/data/states";
import { getMarketStats } from "@/lib/market-stats.functions";
import { getHousingMomentum } from "@/lib/housing-momentum.functions";
import { getCountyInsights } from "@/lib/county-insights.functions";
import { StateProductPage } from "@/components/site/StateProductPage";
import { stateProductFaqJsonLd, stateProductServiceJsonLd, stateProductMetaDescription, buildStateFaq } from "@/components/site/StateProductPage.seo";
import { productHowToJsonLd, productArticleJsonLd } from "@/components/site/ProductPage.seo";
import { buildHead } from "@/lib/seo-head";
import { StrReportCtaBanner } from "@/components/site/StrReportCtaBanner";
import { LiveDscrRateSection } from "@/components/rates/LiveDscrRateSection";
import { stateScenarioLocation } from "@/lib/state-location";


const SLUG = "airbnb-short-term-rental-loans";
const PRODUCT = getProduct(SLUG)!;
const HERO = getProductHero(SLUG)!;

export const Route = createFileRoute("/airbnb-short-term-rental-loans/$state/")({
  loader: async ({ params }) => {
    const state = getState(params.state);
    if (!state) throw notFound();
    const [liveStats, housingMomentum, countyInsights] = await Promise.all([
      getMarketStats({ data: { stateSlug: params.state } }).catch(() => null),
      getHousingMomentum({ data: { stateAbbrev: state.abbrev, stateName: state.name } }).catch(() => null),
      getCountyInsights({ data: { stateAbbrev: state.abbrev, placeLabel: state.name } }).catch(() => null),
    ]);
    return { state, liveStats, housingMomentum, countyInsights };
  },
  head: ({ params, loaderData }) => {
    const state = loaderData?.state;
    if (!state) return { meta: [{ title: `${PRODUCT.shortName} loans | Simply Approved Mortgages` }, { name: "robots", content: "noindex" }] };
    const url = `/${SLUG}/${params.state}`;
    const stateHero = getStateHero(params.state) ?? HERO;
    const title = `${PRODUCT.shortName} in ${state.name} | Simply Approved`;
    const description = stateProductMetaDescription(PRODUCT, state);
    return buildHead({
      title,
      description,
      url,
      keywords: stateKeywordsMeta(PRODUCT.slug, state.name),
      image: stateHero.src,
      imageAlt: stateHero.alt,
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: PRODUCT.shortName, url: `/${SLUG}` },
        { name: state.name, url },
      ],
      faqs: buildStateFaq(PRODUCT, state),
      service: { name: `${PRODUCT.shortName} Loans`, state: state.name },
      extraJsonLd: [
        stateProductServiceJsonLd(PRODUCT, state, url),
        productHowToJsonLd(PRODUCT, url),
        productArticleJsonLd(PRODUCT, url),
      ],
    });
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { state, liveStats, housingMomentum, countyInsights } = Route.useLoaderData();
  return (
    <StateProductPage
      product={PRODUCT}
      state={state}
      liveStats={liveStats}
      housingMomentum={housingMomentum}
      countyInsights={countyInsights}
      topSlot={
        <LiveDscrRateSection
          strategyCards
          bare
          id="live-rates"
          eyebrow={`Live pricing · ${state.name}`}
          heading={`Live long-term DSCR pricing for a ${state.name} rental scenario`}
          paragraphs={[
            `These are actual pricing responses returned for a sample business-purpose DSCR scenario on non-owner-occupied investment property in ${state.name} - rate, APR, borrower points or lender credit, estimated principal and interest, and lock period from the same quote. Pricing changes intraday and is not a loan approval, commitment, or guarantee of financing.`,
          ]}
          scenarioOverrides={{ ...stateScenarioLocation(state.abbrev) }}
          toolLabel={`${state.name} STR DSCR`}
          links={[
            { to: "/investment-property-rates", label: "Price your own DSCR scenario" },
            { to: "/rates", label: "See today's investor rate bands" },
          ]}
        />
      }
      midSlot={<StrReportCtaBanner variant="full" locationLabel={state.name} />}

    />

  );
}
